/**
 * Asset types (RB §4.3, §4.4.3, §4.5).
 *
 * The rulebook's columns are *typical* values — a scenario may give a specific
 * satellite more delta-V or a different signature. What is **not** negotiable per
 * type is the mission allow-list and the sensor class: those decide what the
 * thing fundamentally is.
 *
 * Two capacities, per decision D4 and errata #12: `typicalCapacity` is missions
 * per turn (RB §4.1), and `typicalTtcCapacity` is how many satellites a control
 * station can control. One number cannot express both.
 *
 * `deployCapacity` follows errata #13's own analogy — sub-satellite capacity works
 * "like magazines for ground units", i.e. it is a scenario-set integer. The type
 * table therefore declares 0 everywhere and the scenario grants it per asset.
 */
const ORBITAL_BASICS = [
    'PHASE_SHIFT',
    'TRANSFER',
    'EMISSION_CONTROL',
    'BREAK_CUSTODY',
    'EVADE',
];
const SDA_MISSIONS = [
    'WIDE_SEARCH',
    'SENSOR_TASK',
    'CUSTODY_WATCH',
    'CHARACTERIZE',
    'DEBRIS_SURVEY',
];
const ISR_IMAGING_MISSIONS = [
    'AREA_SEARCH',
    'POINT_COLLECTION',
    'DWELL',
    'TARGET_DEVELOPMENT',
    'BATTLE_DAMAGE_ASSESSMENT',
    'PATTERN_OF_LIFE',
];
const EW_SENSING_MISSIONS = ['ELECTRONIC_SUPPORT', 'ELINT'];
/**
 * The EW effects missions (RB §5.5.2), which arrived with M3.
 *
 * Electronic Protection and Spoof are *soft* missions any electromagnetic platform
 * can run — RB §5.5.2 restricts neither by platform, unlike Dazzle ("ground
 * dazzler only") and unlike jamming, whose reach is a range table (RB §5.5.3).
 */
const EW_EFFECTS_MISSIONS = ['ELECTRONIC_PROTECTION', 'SPOOF'];
function type(def) {
    return Object.freeze(def);
}
const SPACE = [
    type({
        id: 'PNT_SATELLITE',
        name: 'PNT satellite',
        segment: 'SPACE',
        typicalBands: ['MEO'],
        signature: 'LARGE',
        typicalDeltaV: 130,
        typicalFlags: ['DUAL_USE'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: true,
        constellation: 'PNT',
        missions: ORBITAL_BASICS,
    }),
    type({
        id: 'ISR_SATELLITE_OPTICAL',
        name: 'ISR satellite, optical',
        segment: 'SPACE',
        typicalBands: ['LEO'],
        signature: 'NORMAL',
        typicalDeltaV: 130,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'EO_IMAGING',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: true,
        constellation: null,
        missions: [...ISR_IMAGING_MISSIONS, ...ORBITAL_BASICS],
    }),
    type({
        id: 'ISR_SATELLITE_RADAR',
        name: 'ISR satellite, radar',
        segment: 'SPACE',
        typicalBands: ['LEO'],
        signature: 'NORMAL',
        typicalDeltaV: 130,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'RADAR_IMAGING',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: true,
        constellation: null,
        missions: [...ISR_IMAGING_MISSIONS, ...ORBITAL_BASICS],
    }),
    type({
        id: 'SIGINT_SATELLITE',
        name: 'SIGINT satellite',
        segment: 'SPACE',
        typicalBands: ['LEO', 'GEO'],
        signature: 'LOW',
        typicalDeltaV: 130,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'SIGINT_SAT',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...EW_SENSING_MISSIONS, ...EW_EFFECTS_MISSIONS, ...ORBITAL_BASICS],
    }),
    type({
        id: 'MISSILE_WARNING_SATELLITE',
        name: 'Missile warning satellite',
        segment: 'SPACE',
        typicalBands: ['GEO', 'HEO'],
        signature: 'LARGE',
        typicalDeltaV: 120,
        typicalFlags: ['NUCLEAR_ASSOCIATED'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: true,
        constellation: 'MISSILE_WARNING',
        missions: ORBITAL_BASICS,
    }),
    type({
        id: 'SDA_SATELLITE',
        name: 'SDA satellite',
        segment: 'SPACE',
        typicalBands: ['LEO', 'GEO'],
        signature: 'NORMAL',
        typicalDeltaV: 180,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'SDA_SAT',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: true,
        constellation: null,
        missions: [...SDA_MISSIONS, ...ORBITAL_BASICS],
    }),
    type({
        id: 'IMAGING_SATELLITE_EO',
        name: 'Imaging satellite, electro-optical',
        segment: 'SPACE',
        typicalBands: ['LEO'],
        signature: 'NORMAL',
        typicalDeltaV: 130,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'EO_IMAGING',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...ISR_IMAGING_MISSIONS, ...ORBITAL_BASICS],
    }),
    type({
        id: 'IMAGING_SATELLITE_RADAR',
        name: 'Imaging satellite, radar',
        segment: 'SPACE',
        typicalBands: ['LEO'],
        signature: 'NORMAL',
        typicalDeltaV: 130,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'RADAR_IMAGING',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...ISR_IMAGING_MISSIONS, ...ORBITAL_BASICS],
    }),
    type({
        id: 'IMAGING_SATELLITE_HYPERSPECTRAL',
        name: 'Imaging satellite, hyperspectral',
        segment: 'SPACE',
        typicalBands: ['LEO'],
        signature: 'NORMAL',
        typicalDeltaV: 130,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'HYPERSPECTRAL',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...ISR_IMAGING_MISSIONS, ...ORBITAL_BASICS],
    }),
    type({
        id: 'GMTI_RADAR_SATELLITE',
        name: 'GMTI radar satellite',
        segment: 'SPACE',
        typicalBands: ['LEO'],
        signature: 'NORMAL',
        typicalDeltaV: 130,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'GMTI',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...ISR_IMAGING_MISSIONS, ...ORBITAL_BASICS],
    }),
    type({
        id: 'COMMUNICATIONS_RELAY',
        name: 'Communications relay',
        segment: 'SPACE',
        typicalBands: ['GEO'],
        signature: 'LARGE',
        typicalDeltaV: 120,
        typicalFlags: ['DUAL_USE'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        // A relay can itself be a TT&C station (RB §4.6) — which is why the
        // dependency graph has to be resolved to a fixed point (§5.2).
        typicalTtcCapacity: 4,
        hva: true,
        constellation: null,
        missions: [...ORBITAL_BASICS, 'TTC_REASSIGN'],
    }),
    type({
        id: 'ORBITAL_JAMMER',
        name: 'Orbital jammer',
        segment: 'SPACE',
        typicalBands: ['LEO', 'MEO', 'GEO', 'HEO'],
        signature: 'LOW',
        typicalDeltaV: 180,
        typicalFlags: ['EMITTER'],
        optionalFlags: ['COVERT'],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...ORBITAL_BASICS, 'JAMMING', ...EW_EFFECTS_MISSIONS],
    }),
    type({
        id: 'RPO_VEHICLE',
        name: 'RPO / inspector vehicle',
        segment: 'SPACE',
        typicalBands: ['LEO', 'MEO', 'GEO', 'HEO'],
        signature: 'LOW',
        typicalDeltaV: 330,
        typicalFlags: [],
        optionalFlags: ['COVERT'],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...ORBITAL_BASICS, 'RPO', 'SHADOW', 'DEPLOY'],
    }),
    type({
        id: 'COORBITAL_KKV',
        name: 'Co-orbital KKV',
        segment: 'SPACE',
        typicalBands: ['LEO', 'MEO', 'GEO', 'HEO'],
        signature: 'LOW',
        typicalDeltaV: 370,
        typicalFlags: ['COVERT'],
        optionalFlags: ['DISGUISED'],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...ORBITAL_BASICS, 'SHADOW', 'ENGAGE', 'DEPLOY'],
    }),
    type({
        id: 'WEATHER_SATELLITE',
        name: 'Weather satellite',
        segment: 'SPACE',
        typicalBands: ['LEO'],
        signature: 'NORMAL',
        typicalDeltaV: 120,
        typicalFlags: ['DUAL_USE'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ORBITAL_BASICS,
    }),
    type({
        id: 'SUB_SATELLITE',
        name: 'Deployed sub-satellite',
        segment: 'SPACE',
        typicalBands: ['LEO', 'MEO', 'GEO', 'HEO'],
        signature: 'LOW',
        typicalDeltaV: 100,
        typicalFlags: ['COVERT'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...ORBITAL_BASICS, 'ENGAGE'],
    }),
];
const GROUND = [
    type({
        id: 'SDA_RADAR_SITE',
        name: 'SDA radar site',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'GROUND_RADAR',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 2,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: SDA_MISSIONS,
    }),
    type({
        id: 'SDA_OPTICAL_SITE',
        name: 'SDA optical site',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'GROUND_OPTICAL',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: SDA_MISSIONS,
    }),
    type({
        id: 'PASSIVE_RF_SITE',
        name: 'Passive RF surveillance site',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'PASSIVE_RF',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...EW_SENSING_MISSIONS, ...EW_EFFECTS_MISSIONS],
    }),
    type({
        id: 'TTC_STATION',
        name: 'Satellite control station (TT&C)',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 3,
        typicalTtcCapacity: 12,
        hva: false,
        constellation: null,
        missions: ['TTC_REASSIGN'],
    }),
    type({
        id: 'DOWNLINK_STATION',
        name: 'Data downlink / gateway station',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 2,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [],
    }),
    type({
        id: 'MISSILE_WARNING_GROUND_STATION',
        name: 'Missile warning ground station',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: 'MISSILE_WARNING',
        missions: [],
    }),
    type({
        id: 'GROUND_JAMMER_FIXED',
        name: 'Ground jammer, fixed high-power',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ['JAMMING', ...EW_EFFECTS_MISSIONS],
    }),
    type({
        id: 'GROUND_JAMMER_MOBILE',
        name: 'Ground jammer, mobile',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'MOBILE',
        damageClass: 'POINT',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ['JAMMING', 'RELOCATE', ...EW_EFFECTS_MISSIONS],
    }),
    type({
        id: 'GROUND_DAZZLER',
        name: 'Ground-based dazzler',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'RELOCATABLE',
        damageClass: 'POINT',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ['DAZZLE', 'RELOCATE', ...EW_EFFECTS_MISSIONS],
    }),
    type({
        id: 'DA_ASAT_BATTERY',
        name: 'DA-ASAT battery',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'RELOCATABLE',
        damageClass: 'POINT',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ['LAUNCH', 'RELOCATE'],
    }),
    type({
        id: 'BALLISTIC_MISSILE_TEL',
        name: 'Ballistic missile TEL',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: ['NUCLEAR_ASSOCIATED'],
        sensorClass: 'NONE',
        mobility: 'MOBILE',
        damageClass: 'POINT',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ['LAUNCH', 'RELOCATE'],
    }),
    type({
        id: 'BALLISTIC_MISSILE_SILO',
        name: 'Ballistic missile silo',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: ['NUCLEAR_ASSOCIATED'],
        sensorClass: 'NONE',
        mobility: 'STATIC',
        damageClass: 'POINT',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ['LAUNCH'],
    }),
    type({
        id: 'HGV_LAUNCHER',
        name: 'HGV launcher',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'MOBILE',
        damageClass: 'POINT',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ['LAUNCH', 'RELOCATE'],
    }),
    type({
        id: 'INTERCEPTOR_BATTERY',
        name: 'Missile interceptor battery',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: ['EMITTER'],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 2,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: ['INTERCEPT'],
    }),
    type({
        id: 'SPACE_LAUNCH_SITE',
        name: 'Space launch site / range',
        segment: 'GROUND',
        typicalBands: [],
        signature: 'NORMAL',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: 'STATIC',
        damageClass: 'FACILITY',
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [],
    }),
];
/**
 * RB §4.5 — cyber units do not appear on the map and cannot be struck kinetically.
 *
 * The mission lists here were written in M1 against the subset of RB §5.6 that
 * existed as mission ids at the time. M3 completes them: Reconnoiter and Establish
 * Persistence on the offensive side, Monitor and Restore on the defensive.
 */
const CYBER_OFFENSIVE_MISSIONS = [
    'RECONNOITER',
    'GAIN_ACCESS',
    'ESCALATE_PRIVILEGE',
    'DEPLOY_PAYLOAD',
    'ESTABLISH_PERSISTENCE',
];
const CYBER_DEFENSIVE_MISSIONS = [
    'HUNT',
    'HARDEN',
    'MONITOR',
    'ISOLATE',
    'RESTORE',
];
const CYBER = [
    type({
        id: 'CYBER_OFFENSIVE',
        name: 'Offensive cyber unit',
        segment: 'CYBER',
        typicalBands: [],
        signature: 'LOW',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: CYBER_OFFENSIVE_MISSIONS,
    }),
    type({
        id: 'CYBER_DEFENSIVE',
        name: 'Defensive cyber unit',
        segment: 'CYBER',
        typicalBands: [],
        signature: 'LOW',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: CYBER_DEFENSIVE_MISSIONS,
    }),
    type({
        id: 'CYBER_COMBINED',
        name: 'Combined cyber unit',
        segment: 'CYBER',
        typicalBands: [],
        signature: 'LOW',
        typicalDeltaV: null,
        typicalFlags: [],
        optionalFlags: [],
        sensorClass: 'NONE',
        mobility: null,
        damageClass: null,
        typicalCapacity: 1,
        typicalTtcCapacity: null,
        hva: false,
        constellation: null,
        missions: [...CYBER_DEFENSIVE_MISSIONS, ...CYBER_OFFENSIVE_MISSIONS],
    }),
];
export const ASSET_TYPE_LIST = Object.freeze([
    ...SPACE,
    ...GROUND,
    ...CYBER,
]);
export const ASSET_TYPES = Object.freeze(Object.fromEntries(ASSET_TYPE_LIST.map((t) => [t.id, t])));
/**
 * The built-in table only. Prefer `assetTypeIn(world.assetTypes, id)` anywhere a
 * world is in hand — a scenario may have declared types this does not know, and a
 * lookup that misses one silently degrades to the generic behaviour instead of
 * failing.
 */
export function assetTypeDef(id) {
    return ASSET_TYPES[id];
}
/** A type from a resolved table (`WorldState.assetTypes`). */
export function assetTypeIn(types, id) {
    return types.find((t) => t.id === id);
}
/**
 * `assetTypeIn` against whatever is holding the table — a `WorldState`, a
 * `ValidationWorld`, a `BeliefWorld`.
 *
 * Structural on purpose: the belief world the order form validates against must
 * resolve types exactly as truth does, or a scenario-declared launcher would pass
 * submission and fail resolution, which is the one split §6 says must never open.
 */
export function assetTypeOf(world, id) {
    return assetTypeIn(world.assetTypes, id);
}
/**
 * The scenario's asset types merged over the built-in table (§10.4.2).
 *
 * Three rules, and the second is the one that matters:
 *
 *  - an authored row with a **new** id is added, inheriting from `extends` if it
 *    names one and standing alone if it does not;
 *  - an authored row **whose id is a built-in type supersedes it**, so a scenario
 *    can retune a TEL for a theatre without inventing a new name for it;
 *  - the result is sorted by id, so it is stable in a snapshot and in a diff.
 *
 * Deliberately a *merge*, not a replacement: a scenario that declares one launcher
 * type still gets the other thirty-odd types, exactly as `catalogueOf` gives it the
 * seven generic MW rows. An author extends the table; they never restate it.
 */
export function assetTypesOf(authored) {
    const merged = new Map(ASSET_TYPE_LIST.map((t) => [t.id, t]));
    for (const row of authored) {
        const base = merged.get(row.extends ?? row.id);
        const { extends: _extends, ...fields } = row;
        // An unresolvable `extends` is rejected by the validator. Here it degrades to
        // "no base", which produces a well-formed type rather than throwing mid-build.
        merged.set(row.id, Object.freeze({ ...(base ?? FALLBACK_ASSET_TYPE), ...fields, id: row.id }));
    }
    return Object.freeze([...merged.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)));
}
/**
 * What an authored type that extends nothing starts from — inert in every
 * direction. It grants no missions and no sensor, so a typo in `extends` produces
 * an asset that can do nothing rather than one that can silently do everything.
 */
const FALLBACK_ASSET_TYPE = Object.freeze({
    id: '',
    name: '',
    segment: 'GROUND',
    typicalBands: [],
    signature: 'NORMAL',
    typicalDeltaV: null,
    typicalFlags: [],
    optionalFlags: [],
    sensorClass: 'NONE',
    mobility: 'STATIC',
    damageClass: 'POINT',
    typicalCapacity: 1,
    typicalTtcCapacity: null,
    hva: false,
    constellation: null,
    missions: [],
});
//# sourceMappingURL=asset-types.js.map